import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import TimePicker from "./TimePicker";
import {
  MapPin, Plane, QrCode, Clock, FileText, Pencil, Trash2, Package
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const usePurposeOptions = () => {
  const [purposeOptions, setPurposeOptions] = useState(["Overnight", "Off Days", "Annual Leave", "Medical", "Other"]);
  useEffect(() => {
    fetch(`${API}/settings`, { headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` } })
      .then(r => r.json())
      .then(d => { if (d.purpose_options) setPurposeOptions(d.purpose_options); })
      .catch(() => {});
  }, []);
  return purposeOptions;
};

export const PassForm = ({ pass, setPass, onSubmit, submitting, btnLabel }) => {
  const purposeOptions = usePurposeOptions();
  const isOther = pass.purpose_type === "Other" || (pass.purpose_type && !purposeOptions.includes(pass.purpose_type));

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 sm:col-span-1 space-y-2">
          <Label className="text-sm font-semibold">Transport Type *</Label>
          <Select value={pass.transport_type} onValueChange={(v) => setPass({ ...pass, transport_type: v })}>
            <SelectTrigger data-testid="transport-type-select" className="h-11"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              {["Speedboat", "Domestic_Flight", "International_Flight", "Ferry", "Private_Transfer", "Other"].map((t) => (
                <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2 sm:col-span-1 space-y-2">
          <Label className="text-sm font-semibold">Transport Details</Label>
          <Input data-testid="transport-details-input" value={pass.transport_details || ""} onChange={(e) => setPass({ ...pass, transport_details: e.target.value })} placeholder="Flight/boat number" className="h-11" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Destination *</Label>
        <Input data-testid="destination-input" value={pass.destination} onChange={(e) => setPass({ ...pass, destination: e.target.value })} placeholder="Where are you going?" className="h-11" />
      </div>
      <div className="space-y-2">
        <Label className="text-sm font-semibold">Purpose *</Label>
        <Select value={isOther ? "Other" : (pass.purpose_type || "")} onValueChange={(v) => {
          if (v === "Other") {
            setPass({ ...pass, purpose_type: "Other", purpose: "" });
          } else {
            setPass({ ...pass, purpose_type: v, purpose: v });
          }
        }}>
          <SelectTrigger data-testid="purpose-select" className="h-11"><SelectValue placeholder="Select purpose..." /></SelectTrigger>
          <SelectContent>
            {purposeOptions.map((p) => (
              <SelectItem key={p} value={p}>{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isOther && (
          <Input data-testid="purpose-other-input" value={pass.purpose || ""} onChange={(e) => setPass({ ...pass, purpose: e.target.value })} placeholder="Specify purpose..." className="h-11 mt-2" />
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Departure Date *</Label>
          <Input data-testid="departure-date-input" type="date" value={pass.departure_date} onChange={(e) => setPass({ ...pass, departure_date: e.target.value })} className="h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Departure Time *</Label>
          <TimePicker testId="departure-time-input" value={pass.departure_time} onChange={(val) => setPass({ ...pass, departure_time: val })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Return Date *</Label>
          <Input data-testid="arrival-date-input" type="date" value={pass.arrival_date} onChange={(e) => setPass({ ...pass, arrival_date: e.target.value })} className="h-11" />
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-semibold">Return Time *</Label>
          <TimePicker testId="arrival-time-input" value={pass.arrival_time} onChange={(val) => setPass({ ...pass, arrival_time: val })} />
        </div>
      </div>
      <Button type="submit" disabled={submitting} data-testid="submit-pass-btn" className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-12">
        {submitting ? "Submitting..." : btnLabel}
      </Button>
    </form>
  );
};

export const PassCard = ({ pass, getStatusBadge, onViewQR, onEdit, onCancel, canEdit, canCancel }) => (
  <Card className="bg-white border-stone-200 shadow-card hover:shadow-float transition-shadow">
    <CardContent className="p-0">
      <div className="flex flex-col sm:flex-row">
        <div className="flex-1 p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="w-4 h-4 text-primary" strokeWidth={1.5} />
                <h3 className="font-heading font-semibold text-stone-800">{pass.destination}</h3>
              </div>
              <p className="text-sm text-stone-500">{pass.purpose}</p>
            </div>
            {getStatusBadge(pass.status)}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Departure</p>
              <p className="font-medium text-stone-700">{pass.departure_date}</p>
              <p className="text-stone-500">{pass.departure_time}</p>
            </div>
            <div>
              <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Return</p>
              <p className="font-medium text-stone-700">{pass.arrival_date}</p>
              <p className="text-stone-500">{pass.arrival_time}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-100">
            <Plane className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
            <span className="text-sm text-stone-600 capitalize">{pass.transport_type?.replace(/_/g, " ")}</span>
            {pass.transport_details && <span className="text-sm text-stone-400">- {pass.transport_details}</span>}
          </div>
          {(pass.status === "cancelled" || pass.status === "rejected") && pass.cancel_note && (
            <div className="mt-3 bg-stone-50 rounded-lg p-3 text-sm">
              <p className="text-xs text-stone-400 font-semibold uppercase mb-1">{pass.status === "rejected" ? "Rejection Reason" : "Cancellation Reason"}</p>
              <p className="text-stone-600">{pass.cancel_note}</p>
            </div>
          )}
          {(canEdit || canCancel) && (
            <div className="flex gap-2 mt-3 pt-3 border-t border-stone-100">
              {canEdit && (
                <Button variant="outline" size="sm" onClick={onEdit} className="gap-1.5 text-xs" data-testid={`edit-pass-${pass.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </Button>
              )}
              {canCancel && (
                <Button variant="outline" size="sm" onClick={onCancel} className="gap-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" data-testid={`cancel-pass-${pass.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Cancel
                </Button>
              )}
            </div>
          )}
        </div>
        {pass.status === "approved" && pass.qr_code && (
          <div className="border-t sm:border-t-0 sm:border-l border-dashed border-stone-200 p-4 flex flex-col items-center justify-center bg-stone-50/50">
            <Button variant="ghost" onClick={onViewQR} data-testid={`view-qr-${pass.id}`} className="flex flex-col items-center gap-2 hover:bg-primary/5 p-4 rounded-xl">
              <div className="w-16 h-16 bg-white rounded-xl shadow-inner flex items-center justify-center">
                <QrCode className="w-8 h-8 text-primary" strokeWidth={1.5} />
              </div>
              <span className="text-xs font-medium text-primary">View QR Code</span>
            </Button>
          </div>
        )}
        {pass.status === "pending" && (
          <div className="border-t sm:border-t-0 sm:border-l border-dashed border-stone-200 p-4 flex flex-col items-center justify-center bg-amber-50/30">
            <div className="text-center">
              <p className="text-xs text-amber-600 font-medium mb-1">Approval Step</p>
              <p className="text-2xl font-heading font-bold text-amber-700">{pass.current_approval_step + 1}</p>
              <p className="text-xs text-amber-500">Awaiting review</p>
            </div>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

export const EmptyState = ({ icon: Icon, message }) => (
  <Card className="border-dashed border-2 border-stone-200">
    <CardContent className="py-12 text-center">
      <div className="w-12 h-12 bg-stone-100 rounded-xl flex items-center justify-center mx-auto mb-3">
        <Icon className="w-6 h-6 text-stone-400" strokeWidth={1.5} />
      </div>
      <p className="text-sm text-stone-500">{message}</p>
    </CardContent>
  </Card>
);

export const BelongingsSection = ({ belongings, testIdPrefix = "my" }) => {
  if (!belongings || belongings.length === 0) return null;
  return (
    <div data-testid={`${testIdPrefix}-belongings-section`}>
      <h3 className="font-heading font-bold text-stone-800 mb-3 flex items-center gap-2">
        <Package className="w-5 h-5 text-primary" strokeWidth={1.5} />
        Registered Belongings
      </h3>
      <Card className="bg-white border-stone-200">
        <CardContent className="p-4">
          <div className="space-y-2">
            {belongings.map((item) => (
              <div key={item.id} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl" data-testid={`${testIdPrefix}-belonging-${item.id}`}>
                <Package className="w-4 h-4 text-primary flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <p className="font-medium text-stone-800 text-sm">{item.name}</p>
                  {item.description && <p className="text-xs text-stone-500">{item.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const getStatusBadge = (status) => {
  const styles = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    used: "bg-stone-100 text-stone-500 border-stone-200",
    cancelled: "bg-stone-100 text-stone-400 border-stone-200",
  };
  return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
};

export const canEditPass = (pass) => pass.status === "pending" && (!pass.approvals || pass.approvals.length === 0) && pass.current_approval_step === 0;
export const canCancelPass = (pass) => pass.status !== "used" && pass.status !== "cancelled" && pass.status !== "rejected";

export const emptyPass = {
  transport_type: "", transport_details: "", destination: "",
  purpose: "", purpose_type: "", departure_date: "", departure_time: "",
  arrival_date: "", arrival_time: "",
};
